/**
 * A server that is unwell must not sign anyone out.
 *
 * About once an hour the Supabase library renews the signed-in person's token. In the version
 * this app uses (2.89), if that request is answered with anything the library does not know to be
 * temporary, it deletes the saved session: the person is signed out for real and has to type
 * their password again. It knows only 502, 503 and 504. So a 500 from the auth service (its own
 * database not answering), or one of the gateway's error pages (520 to 530) when the browser lets
 * the page read it, signs people out although nothing is wrong with their account. Newer versions
 * of the library count all of these as temporary.
 *
 * Until the app moves to one, this stands between the library and the network and reports those
 * answers as 503, the one the library keeps the session for and tries again after. It changes
 * token renewals only, and only when the server says it is unwell (5xx) or busy (429). A refused
 * refresh token (a 400: really signed out) passes through untouched, and so does everything else.
 *
 * Measured with the real client: scripts/diagnostics/what-the-client-says-when-the-server-is-down.cjs
 * Tested with the real client: scripts/client-tests/stays-signed-in.test.mjs
 */
const isTokenRenewal = (input) => {
  const url = String(input?.url ?? input);
  return url.includes('/auth/v1/token') && url.includes('grant_type=refresh_token');
};

const unwellOrBusy = (status) => status === 429 || status >= 500;
const ALREADY_UNDERSTOOD = [502, 503, 504];

export const keepSessionWhenServerIsUnwell = (doFetch) => async (input, init) => {
  const response = await doFetch(input, init);
  if (!isTokenRenewal(input)) return response;
  if (!unwellOrBusy(response.status) || ALREADY_UNDERSTOOD.includes(response.status)) return response;
  return new Response(null, { status: 503, statusText: 'Service Unavailable' });
};

/**
 * A log-out request that is not answered is given up on.
 *
 * The library holds its lock while it waits for the server to confirm a log-out, and everything
 * else that needs the session queues behind that lock: with a server that never answers, the page
 * can do nothing more until the browser itself gives up, minutes later. And if the answer did
 * come late, the library would then remove whatever session is saved by then, which could be the
 * one from a sign-in made in the meantime. Ending the request after a few seconds does away with
 * both. The library reports a failed request and keeps the session; removing it from the device
 * is src/utils/signOut.js's job.
 */
export const LOGOUT_GIVE_UP_AFTER_MS = 4000;

const isLogout = (input) => String(input?.url ?? input).includes('/auth/v1/logout');

export const giveUpOnASlowLogout = (doFetch, afterMs = LOGOUT_GIVE_UP_AFTER_MS) => async (input, init) => {
  if (!isLogout(input)) return doFetch(input, init);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), afterMs);
  try {
    return await doFetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

/**
 * A database read that never answers is given up.
 *
 * Start-up waits for a few reads (the person's account, their subscription). If one of them never
 * gets an answer (a phone that paused the connection, a request lost on the way) start-up never
 * ends: the "Can't reach the server" screen stays, and "Try again" waits for that same read. Codex
 * reproduced exactly this on 15 October 2026 (docs/reviews/nutrition-to-fitness-freeze.md). Ending
 * the read after a while turns it into an ordinary "no answer", which the app already handles: it
 * says so and tries again.
 *
 * Only reads (GET and HEAD) of the database (/rest/v1/). Never a write, and never the sign-in
 * service: ending a token renewal half-way could cost the person their session.
 */
export const READ_GIVE_UP_AFTER_MS = 20000;

const isDatabaseRead = (input, init) => {
  const url = String(input?.url ?? input);
  const method = String(init?.method || input?.method || 'GET').toUpperCase();
  return url.includes('/rest/v1/') && (method === 'GET' || method === 'HEAD');
};

export const giveUpOnAHungRead = (doFetch, afterMs = READ_GIVE_UP_AFTER_MS) => async (input, init) => {
  if (!isDatabaseRead(input, init)) return doFetch(input, init);
  const controller = new AbortController();
  const callerSignal = init?.signal;
  const passOn = () => controller.abort(callerSignal.reason);
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', passOn, { once: true });
  }
  const timer = setTimeout(() => controller.abort(), afterMs);
  try {
    return await doFetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
    if (callerSignal) callerSignal.removeEventListener('abort', passOn);
  }
};

/**
 * A renewal refused because the other app has already renewed.
 *
 * Fitness and the joined Nutrition keep one saved session at one address, each renews it alone, and
 * they hold no lock in common. A renewal spends the refresh token and saves a new one. So a page paused
 * on a phone while its renewal is under way can wake up to a refusal; this library (2.89) then deletes
 * whatever session is saved by then, signing the person out of both apps (31 October 2026, the likely
 * reason the user went empty on switching apps).
 *
 * When the refused token is no longer the saved one, the saved session is what counts, and nothing is
 * sent to the server (Codex's review 079: a second request could cross accounts or undo a log-out):
 * - the same account: the library is answered with the session already saved, as if it had renewed;
 * - another account, or it cannot be told: answered 503, which the library keeps the session for, so
 *   this page does not take on the other person's session. It retries for some seconds, then reports
 *   it could not reach the server.
 * With nothing saved, or the same token saved, the refusal is real and passes through.
 *
 * When upgrading the library, see Nutrition's src/utils/renewalTicket.ts: its newer library discards
 * this answer on purpose, and its start-up reads the session again.
 */
export const REFUSED_CODES = ['refresh_token_already_used', 'refresh_token_not_found'];

export const keepTheNewerSavedSession = ({ readSaved, note = () => {} }) => (doFetch) => async (input, init) => {
  if (!isTokenRenewal(input)) return doFetch(input, init);
  const read = () => { try { return readSaved() ?? null; } catch { return null; } };
  const before = read();
  const response = await doFetch(input, init);
  if (response.status !== 400) return response;
  let code = null;
  let sent = null;
  try {
    code = (await response.clone().json())?.error_code ?? null;
    sent = JSON.parse(init.body).refresh_token;
  } catch {
    return response;
  }
  if (!REFUSED_CODES.includes(code) || !sent) return response;
  const saved = read();
  if (!saved?.refresh_token || saved.refresh_token === sent) {
    note('refused', code);
    return response;
  }
  const sameAccount = before?.refresh_token === sent && before.user?.id && before.user.id === saved.user?.id;
  if (!sameAccount) {
    note('other_account', code);
    return new Response(null, { status: 503, statusText: 'Service Unavailable' });
  }
  note('kept_newer', code);
  // Answered as saved: expires_at says when it really runs out. A missing or zero expires_in would read as "no session"
  return new Response(JSON.stringify({ ...saved, expires_in: saved.expires_in || 3600 }), { status: 200, headers: { 'content-type': 'application/json' } });
};
